import { Link, useForm, usePage } from '@inertiajs/react'
import { CartProvider, Cart } from '../../lib/cart'
import CartHeader from '../../lib/CartHeader'

interface Account { id: number; name: string; slug: string; logo_url?: string | null }
interface Props { account: Account; cart?: Cart; flash?: { notice?: string; alert?: string } }

export default function CatalogUploadProof(props: Props) {
  const { account, cart } = props
  return (
    <CartProvider cart={cart} slug={account.slug}>
      <CatalogUploadProofInner {...props} />
    </CartProvider>
  )
}

function CatalogUploadProofInner({ account }: { account: Account }) {
  const { flash } = usePage().props as { flash?: { notice?: string; alert?: string } }

  const { data, setData, post, processing, errors } = useForm({
    reference_number: new URLSearchParams(window.location.search).get('reference_number')?.toUpperCase() || '',
    payment_proof: null as File | null,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/${account.slug}/catalog/upload-proof`, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        // Inertia redirects back to UploadProof with flash notice
      },
      onError: (err) => {
        console.error('Upload error:', err)
      },
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CartHeader slug={account.slug} accountName={account.name} logoUrl={account.logo_url} />

      <div className="max-w-lg mx-auto px-4 py-12">
        {/* Flash messages */}
        {flash?.notice && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded mb-4 text-sm">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded mb-4 text-sm">
            {flash.alert}
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-xl font-bold text-[#202020] mb-1">Subir comprobante de pago</h1>
          <p className="text-sm text-gray-500 mb-6">
            Ingresa el número de referencia de tu pedido y adjunta tu comprobante de transferencia.
          </p>

          {errors && Object.keys(errors).length > 0 && (
            <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
              {Object.entries(errors).map(([k, v], i) => (
                <p key={i}>{Array.isArray(v) ? v.join(', ') : String(v)}</p>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de referencia *
              </label>
              <input type="text" value={data.reference_number}
                onChange={(e) => setData('reference_number', e.target.value.toUpperCase())}
                placeholder="Ej: 198CSAK6"
                maxLength={8}
                className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-lg tracking-wider text-center focus:outline-none focus:ring-2 focus:ring-[#55ACEE]"
                required />
              <p className="text-xs text-gray-400 mt-1">
                Lo encuentras en la confirmación de tu pedido o en Mis Pedidos.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comprobante *
              </label>
              <input type="file" accept="image/*,.pdf"
                onChange={(e) => setData('payment_proof', e.target.files?.[0] || null)}
                className="w-full text-sm text-gray-700" required />
              <p className="text-xs text-gray-400 mt-1">Imagen o PDF de la transferencia.</p>
            </div>

            <button type="submit" disabled={processing}
              className="w-full bg-[#FFC107] text-[#202020] py-3 rounded font-semibold hover:bg-[#e6ad06] disabled:opacity-50">
              {processing ? 'Subiendo...' : 'Subir comprobante'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link href={`/${account.slug}/catalog`} className="text-sm text-[#55ACEE] hover:underline">
              ← Volver al catálogo
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
